import { hasClassPath, hasNamePath } from './properties';
import {
  type AccountThemeConfig,
  FolderLayout,
  type Slot,
  MediaAdornment,
  MediaAspectRatio,
  ThumbnailSize
} from './types/cit-public-api';

/** Recursively makes every property required (objects are unwrapped recursively). */
type DeepRequired<T> = T extends (...args: unknown[]) => unknown
  ? T
  : T extends object
    ? { [K in keyof T]-?: DeepRequired<T[K]> }
    : T;

export const DEFAULT_FONT = 'Roboto';

interface DefaultWebPublishingThemeLayout extends AccountThemeConfig {
  layout: {
    folderLayout: FolderLayout;
    thumbnailSlots: {
      // our default theme config does not use slot3
      slot1: Slot;
      slot2: Slot;
    };
    thumbnailSizes: ThumbnailSize;
    gap: number;
  };
  // the default theme sets no heroConfig: an absent flag already reads as enabled
  header: Omit<NonNullable<AccountThemeConfig['header']>, 'heroConfig'>;
}

export const DEFAULT_ACCOUNT_THEME: DeepRequired<DefaultWebPublishingThemeLayout> = {
  isTestTheme: false,
  backgroundColor: '#282818',
  bannerTextColor: '#FFFFFF',
  color: {
    titleFontColor: '#FFFFFF',
    bodyFontColor: '#FFFFFF',
    bannerTextColor: '#FFFFFF',
    backgroundColor: '#282818',
    entryCardColor: '#282818'
  },
  layout: {
    folderLayout: FolderLayout.GRID,
    thumbnailSlots: {
      slot1: {
        on: true,
        property: hasNamePath
      },
      slot2: {
        on: true,
        property: hasClassPath
      }
    },
    thumbnailSizes: ThumbnailSize.MEDIUM,
    gap: 20
  },
  style: {
    mediaAdornment: MediaAdornment.SQUARE,
    mediaAspectRatio: MediaAspectRatio.NATURAL
  },
  typography: {
    titleFont: DEFAULT_FONT,
    bodyFont: DEFAULT_FONT,
    lineHeight: 'normal',
    letterSpacing: 'normal',
    baseFontSize: 'medium'
  },
  header: {
    headerFont: DEFAULT_FONT,
    headerFontColor: '#FFFFFF',
    headerBackgroundColor: '#1F2937',
    headerHTML: {
      custom: false,
      html: `
        <div id='header-container' style='background-color: {headerBackgroundColor}'>
          <div
            id='header-top-container'
            style='
              display: flex;
              justify-content: space-between;
              align-items: center;
              min-height: 80px;
            '
          >
            <div
              id='icon-title-container'
              style='display: flex; align-items: center; gap: 0.625rem; margin-left: 1.5rem; color: {headerFontColor}'
            >
              {backButton}
              <a id='icon-link' href='{websiteURL}' style='display: flex'>
                <style>
                  /* sized from a rule rather than a style attribute so the media query can win */
                  #icon {
                    width: 2.25rem;
                    height: 2.25rem;
                    padding: 0.125rem;
                    box-sizing: content-box;
                    border: 0.125rem solid {headerFontColor};
                    border-radius: 50%;
                  }

                  @media (min-width: 768px) {
                    #icon {
                      width: 3rem;
                      height: 3rem;
                    }
                  }

                  /*
                    iconURL is empty when the account has no avatar, and the sanitizer drops an
                    empty src outright, so the img lands here with no src at all and renders as a
                    broken image, border and all. Scoped to the icon on purpose: any other img
                    with no source is a mistake, and should stay visibly broken rather than
                    disappear
                  */
                  #icon:not([src]) {
                    display: none;
                  }
                </style>
                <img src='{iconURL}' alt='{accountName} logo' id='icon' loading='lazy' />
              </a>
              <a
                id='title-link'
                href='{websiteURL}'
                style='min-width: 0; text-decoration: none;'
              >
                <style>
                  /*
                    min-width on the anchor above keeps the line clamp working: the anchor is the flex
                    item, and without it it refuses to shrink below the full name
                  */
                  #title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 1;
                    color: {headerFontColor};
                    font-family: {headerFont};
                    line-height: {lineHeight};
                    letter-spacing: {letterSpacing};
                  }

                  @media (min-width: 768px) {
                    #title {
                      font-size: 1.8rem;
                    }
                  }
                </style>
                <h1 id='title'>{accountName}</h1>
              </a>
            </div>
            <div
              id='search-button-links-container'
              style='display: flex; align-items: center; padding-right: 1.5rem; gap: 1.5rem;'
            >
              {searchButton}
              {linksMenu}
            </div>
          </div>
          <div id='search-bar-container'>
            {searchBar}
          </div>
        </div>
      `
    }
  }
};
